import { format } from "date-fns";
import { batch } from "react-redux";
import {
  type CreateParticipantEventInput,
  type Meeting,
} from "../../mentor/components/schedule-one-on-one/schedule-one-on-one.constants";
import { formatUtcToDate } from "../../shared/formattedDate";
import { getErrorMessageFromRequestError } from "../../shared/helpers/messages/request";
import {
  setError,
  setFetching,
} from "../../shared/request/actions/requestActions";
import {
  CANCEL_SUCCESS_MESSAGE,
  CONFIRM_SUCCESS_MESSAGE,
  CREATE_EVENT_SUCCESS_MESSAGE,
  UPDATE_EVENT_SUCCESS_MESSAGE,
} from "../components/participantsEvents/utils";
import {
  cancelEvent,
  confirmEvent,
  educatorCalendarEvents,
  getEventDetails,
  getEvents,
  getParticipantFAQ,
  getUpcomingEvents,
  getWorkingHours,
  saveEvent,
  updateEvent,
} from "../service";
import {
  ADD_EVENT_TO_CURRENT_CALENDAR,
  ASSOCIATE_SAVING_ACCOUNT,
  CREATE_RETIREMENT_INCOME,
  DELETE_RETIREMENT_INCOME,
  DISABLE_RETIREMENT_INCOME,
  DISSOCIATE_SAVING_ACCOUNT,
  ENABLE_RETIREMENT_INCOME,
  FETCHING_EVENT_DETAIL,
  RESET_RETIREMENT_INCOME_DATA,
  RESTORE_SOCIAL_SECURITY_INCOME,
  SET_AVAILABLE_HOURS,
  SET_CLEAN_EVENTS,
  SET_EDUCATOR_EVENTS,
  SET_EVENT_AS_CANCEL,
  SET_EVENT_AS_CONFIRMED,
  SET_EVENT_DETAILS,
  SET_FAQ,
  SET_IDEAL_RETIREMENT_INCOME,
  SET_LIFE_TIME_INCOME,
  SET_LOADING_PARTICIPANT_EVENTS,
  SET_PARTICIPANTS_EVENTS,
  SET_PARTICIPANT_SAVING_ACCOUNTS,
  SET_RETIREMENT_INCOME_DATA,
  SET_UPCOMING_EVENTS,
  SET_UTC_DETAILS,
  SET_WORKING_HOURS,
  TOGGLE_EVENT_MODAL,
  TOGGLE_ONE_ON_ONE_MODAL,
  UPDATE_RETIREMENT_INCOME,
  UPDATE_SOCIAL_SECURITY_INCOME,
} from "./actionsTypes";

export const setParticipantSavingAccounts = (savingAccountsData) => ({
  type: SET_PARTICIPANT_SAVING_ACCOUNTS,
  payload: savingAccountsData,
});

export const associateSavingAccount = (data) => ({
  type: ASSOCIATE_SAVING_ACCOUNT,
  payload: data,
});

export const dissociateSavingAccount = (data) => ({
  type: DISSOCIATE_SAVING_ACCOUNT,
  payload: data,
});

export const setRetirementIncomeData = (data) => ({
  type: SET_RETIREMENT_INCOME_DATA,
  payload: data,
});

export const resetRetirementIncomeData = () => ({
  type: RESET_RETIREMENT_INCOME_DATA,
});

export const addRetirementIncome = (data) => ({
  type: CREATE_RETIREMENT_INCOME,
  payload: data,
});

export const updateRetirementIncome = (data) => ({
  type: UPDATE_RETIREMENT_INCOME,
  payload: data,
});

export const deleteRetirementIncome = (data) => ({
  type: DELETE_RETIREMENT_INCOME,
  payload: data,
});

export const enableRetirementIncome = (data) => ({
  type: ENABLE_RETIREMENT_INCOME,
  payload: data,
});

export const disableRetirementIncome = (data) => ({
  type: DISABLE_RETIREMENT_INCOME,
  payload: data,
});

export const setIdealRetirementIncome = (data) => ({
  type: SET_IDEAL_RETIREMENT_INCOME,
  payload: data,
});

export const restoreSocialSecurityIncome = (data) => ({
  type: RESTORE_SOCIAL_SECURITY_INCOME,
  payload: data,
});

export const updateSocialSecurityIncome = (data) => ({
  type: UPDATE_SOCIAL_SECURITY_INCOME,
  payload: data,
});

export const setLifeTimeIncome = (data) => ({
  type: SET_LIFE_TIME_INCOME,
  payload: data,
});

export const setParticipantEvents = ({ events }) => ({
  type: SET_PARTICIPANTS_EVENTS,
  payload: { events },
});

export const setEducatorEvents = ({ events }) => ({
  type: SET_EDUCATOR_EVENTS,
  payload: { events },
});

export const setParticipantEventDetails = ({ eventDetails }) => ({
  type: SET_EVENT_DETAILS,
  payload: { eventDetails },
});

export const setUpcomingEvents = (data) => ({
  type: SET_UPCOMING_EVENTS,
  payload: data,
});

export const setParticipantWorkingHours = ({ workingHours }) => ({
  type: SET_WORKING_HOURS,
  payload: { workingHours },
});

export const setAvailableHours = ({ hours }) => ({
  type: SET_AVAILABLE_HOURS,
  payload: { hours },
});

export const setUtcParticipantDetails = ({ utcDetails }) => ({
  type: SET_UTC_DETAILS,
  payload: { utcDetails },
});

export const setMarkEventAsConfirmed = (data) => ({
  type: SET_EVENT_AS_CONFIRMED,
  payload: data,
});

export const setEventAsCancel = (data) => ({
  type: SET_EVENT_AS_CANCEL,
  payload: data,
});

export const setParticipantFaq = (faq) => ({
  type: SET_FAQ,
  payload: faq,
});

export const setLoadingParticipantEvents = ({ isLoaded }) => ({
  type: SET_LOADING_PARTICIPANT_EVENTS,
  payload: { isLoaded },
});

export const setAddEventCurrentCalendar = (event) => ({
  type: ADD_EVENT_TO_CURRENT_CALENDAR,
  payload: { event },
});

export const setToggleEventModal = ({ eventId }) => ({
  type: TOGGLE_EVENT_MODAL,
  payload: { eventId },
});

export const setFetchingEventDetail = ({ isFetching }) => ({
  type: FETCHING_EVENT_DETAIL,
  payload: { isFetching },
});

export const setToggleOneOnOneModal = ({ eventId, open }) => ({
  type: TOGGLE_ONE_ON_ONE_MODAL,
  payload: { eventId, open },
});

export const asyncCleanEvents = () => ({
  type: SET_CLEAN_EVENTS,
});

export const asyncGetEvents = (params) => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));
  dispatch(setLoadingParticipantEvents({ isLoaded: false }));
  try {
    const response = await getEvents(params);
    const { data } = response.data.data;
    const events = data
      .filter(({ eventStatus, eventType }) => {
        return !(
          eventType.eventTypeId !== 1 && eventStatus.eventStatusId === 2
        );
      })
      .map((event) => ({
        ...event,
        title: `${format(formatUtcToDate(event.start), "h:mm a")} ${
          event.title
        }`,
        start: formatUtcToDate(event.start),
        end: formatUtcToDate(event.end),
      }));

    dispatch(setParticipantEvents({ events }));
    dispatch(setLoadingParticipantEvents({ isLoaded: true }));
  } catch (error) {
    const message = getErrorMessageFromRequestError(error);
    dispatch(setError({ isOpen: true, message, type: "error" }));
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

export const asyncGetEducatorEvents = (params) => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));
  try {
    const response = await educatorCalendarEvents(params);
    const { data } = response.data;
    dispatch(setEducatorEvents({ events: data }));
  } catch (error) {
    const message = getErrorMessageFromRequestError(error);
    dispatch(setError({ isOpen: true, message, type: "error" }));
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

export const asyncGetUpcomingEvents = () => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));
  try {
    const response = await getUpcomingEvents();
    const events = response.data.data.map((event) => ({
      eventId: event.eventId,
      title: event.title,
      start: formatUtcToDate(event.start),
      end: formatUtcToDate(event.end),
      eventType: {
        eventTypeId: event.eventType.eventTypeId,
      },
      eventStatus: {
        eventStatusId: event.eventStatus.eventStatusId,
      },
      allDay: false,
      resource: {
        ...event,
      },
    }));
    dispatch(setUpcomingEvents(events));
  } catch (error) {
    const message = getErrorMessageFromRequestError(error);

    dispatch(setError({ isOpen: true, message, type: "error" }));
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

type SaveEventParams = {
  eventInput: CreateParticipantEventInput;
  meeting?: Meeting;
};

export const asyncSaveEvents =
  ({ eventInput, meeting }: SaveEventParams) =>
  async (dispatch) => {
    dispatch(setFetching({ isFetching: true }));

    let message;

    try {
      let eventResponse;

      if (eventInput.eventId) {
        eventResponse = await updateEvent({ eventDetails: eventInput });
        message = UPDATE_EVENT_SUCCESS_MESSAGE;
      } else {
        eventResponse = await saveEvent({ eventDetails: eventInput });
        message = CREATE_EVENT_SUCCESS_MESSAGE;
      }

      batch(() => {
        dispatch(asyncGetUpcomingEvents());
        dispatch(
          setAddEventCurrentCalendar({
            ...eventResponse.data.data,
            start: meeting.start,
            end: meeting.end,
          })
        );
        dispatch(
          setError({
            isOpen: true,
            message,
            type: "success",
          })
        );
      });
    } catch (error) {
      message = getErrorMessageFromRequestError(error);
      dispatch(
        setError({
          isOpen: true,
          statusCode: error?.response?.status || 400,
          message,
          type: "error",
        })
      );
    } finally {
      dispatch(setFetching({ isFetching: false }));
    }
  };

export const asyncCancelEvent =
  ({ eventId }: { eventId: string }) =>
  async (dispatch) => {
    dispatch(setFetching({ isFetching: true }));
    let message = CANCEL_SUCCESS_MESSAGE;
    try {
      await cancelEvent({ eventId });

      batch(() => {
        dispatch(
          setError({
            isOpen: true,
            message,
            type: "success",
          })
        );
        dispatch(setEventAsCancel({ eventId }));
      });
    } catch (error) {
      message = getErrorMessageFromRequestError(error);
      dispatch(setError({ isOpen: true, message, type: "error" }));
    } finally {
      dispatch(setFetching({ isFetching: false }));
    }
  };

export const asyncConfirmEvent =
  ({ eventId, onSuccess }: { eventId: string; onSuccess?: () => void }) =>
  async (dispatch) => {
    dispatch(setFetching({ isFetching: true }));
    let message = CONFIRM_SUCCESS_MESSAGE;
    try {
      await confirmEvent({ eventId });
      dispatch(
        setError({
          isOpen: true,
          message,
          type: "success",
        })
      );
      dispatch(
        setMarkEventAsConfirmed({
          eventId,
        })
      );
      onSuccess?.();
    } catch (error) {
      message = getErrorMessageFromRequestError(error);
      dispatch(setError({ isOpen: true, message, type: "error" }));
    } finally {
      dispatch(setFetching({ isFetching: false }));
    }
  };

export const asyncEventDetails =
  ({ eventId }) =>
  async (dispatch, getState) => {
    dispatch(setFetching({ isFetching: true }));
    dispatch(setFetchingEventDetail({ isFetching: true }));
    const currentState = getState();
    const { profileId } = currentState.profile.participantProfileId;
    let message = "";
    try {
      const eventStatus = [];
      const response = await getEventDetails({ eventId });
      const { data } = response.data;

      data.guest.guestList.forEach((row) => {
        if (row.profileId === profileId) eventStatus.push(row);
      });

      const eventDetails = {
        ...data,
        start: formatUtcToDate(data.start),
        end: formatUtcToDate(data.end),
      };
      dispatch(setParticipantEventDetails({ eventDetails }));
      dispatch(
        setError({
          isOpen: true,
          message,
          type: "success",
        })
      );
    } catch (error) {
      message = getErrorMessageFromRequestError(error);
      dispatch(setError({ isOpen: true, message, type: "error" }));
    } finally {
      dispatch(setFetching({ isFetching: false }));
      dispatch(setFetchingEventDetail({ isFetching: false }));
    }
  };

export const asyncGetWorkingHours = () => async (dispatch, getSate) => {
  const currentState = getSate();
  const { educatorId } = currentState.profile;
  dispatch(setFetching({ isFetching: true }));
  try {
    if ([null, undefined].includes(educatorId)) {
      return;
    }
    const { data } = await getWorkingHours({ educatorId });
    const daysAvailable = data.data.workingHours.reduce((acc, slot) => {
      if (!acc[slot.workingDay]) {
        acc[slot.workingDay] = [];
      }
      acc[slot.workingDay].push(slot);
      return acc;
    }, {});
    dispatch(
      setParticipantWorkingHours({
        workingHours: {
          utc: data.data.utc,
          enabled: data.data.enabled,
          workingHours: data.data.workingHours,
          daysAvailable,
        },
      })
    );
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

export const asyncGetOrganizationFaq = (organizationId) => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));
  try {
    const response = await getParticipantFAQ(organizationId);
    dispatch(setParticipantFaq(response.data.data));
  } catch (error) {
    let message = "";
    if (error.response) {
      if (error.response.data.errors)
        message = error.response.data.errors.toString();
      else message = "Unexpected error";
    }
    dispatch(setError({ isOpen: true, message, type: "error" }));
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};
